import React, { memo, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { EmptyData, fmtPrice, signed } from "./shared";

/* The buyer's go/no-go checklist — the server-computed gates behind the final
 * decision, rendered verbatim so a NO TRADE is always explainable. */
export const BuyerChecklist = memo(function BuyerChecklist({ view }) {
  const items = view?.checklist || [];
  const side = view?.action?.side;
  const noTrade = view?.noTradeReasons || [];
  const title = side === "CALL" || (side !== "PUT" && view?.direction === "BULLISH")
    ? "CALL entry checklist" : side === "PUT" || view?.direction === "BEARISH"
    ? "PUT entry checklist" : "Entry checklist";
  if (!items.length) {
    return (
      <div className="p-4">
        <p className="text-sm font-black text-slate-300">NO DIRECTIONAL LEAN YET</p>
        <p className="mt-1 text-[10px] leading-4 text-slate-500">
          The checklist appears once price picks a side (above/below VWAP with EMA alignment).
          Until then the correct trade is no trade.
        </p>
        {noTrade.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {noTrade.map((r) => (
              <p key={r} className="flex items-start gap-1.5 text-[10px] text-amber-300/90">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{r}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  }
  const passed = items.filter((c) => c.pass).length;
  return (
    <div className="p-3">
      <div className="mb-2 flex items-center justify-between px-1">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{title}</p>
        <span className={`text-[10px] font-black ${passed === items.length ? "text-emerald-300" : "text-slate-400"}`}>{passed}/{items.length}</span>
      </div>
      <div className="space-y-1">
        {items.map((c) => (
          <div key={c.label}
            className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
              c.pass ? "border-emerald-500/15 bg-emerald-500/[.05]"
              : c.required ? "border-rose-500/25 bg-rose-500/[.06]" : "border-slate-800 bg-slate-950/40"}`}>
            {c.pass
              ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
              : <AlertTriangle className={`h-3.5 w-3.5 shrink-0 ${c.required ? "text-rose-400" : "text-slate-500"}`} />}
            <span className={`flex-1 text-[11px] ${c.pass ? "text-slate-300" : c.required ? "text-rose-200" : "text-slate-500"}`}>{c.label}</span>
            {c.required && !c.pass && <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-black uppercase text-rose-300">required</span>}
          </div>
        ))}
      </div>
      {noTrade.length > 0 && (
        <div className="mt-2 space-y-1 border-t border-slate-800/70 pt-2">
          {noTrade.slice(0, 4).map((r) => (
            <p key={r} className="flex items-start gap-1.5 text-[10px] text-amber-300/90">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{r}
            </p>
          ))}
        </div>
      )}
    </div>
  );
});

/* Long-premium quality rank per side: liquidity + spread + VWAP + delta + theta. */
export const SmartStrikeSelector = memo(function SmartStrikeSelector({ data, preferredSide }) {
  const [side, setSide] = useState(preferredSide || "CE");
  useEffect(() => { if (preferredSide) setSide(preferredSide); }, [preferredSide]);
  const rows = data?.strike_rankings?.[side] || [];
  return (
    <div>
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2">
        <p className="text-[10px] text-slate-600">Liquidity + spread + VWAP + delta + theta</p>
        <div className="flex rounded-lg bg-slate-950 p-0.5">
          {["CE", "PE"].map((key) => <button key={key} onClick={() => setSide(key)} className={`rounded-md px-2.5 py-1 text-[10px] font-black ${side === key ? (key === "CE" ? "bg-emerald-400 text-slate-950" : "bg-rose-400 text-slate-950") : "text-slate-600"}`}>{key}</button>)}
        </div>
      </div>
      {rows.length ? <div className="divide-y divide-slate-800/80">
        {rows.map((row, index) => {
          const velocity = row.velocity?.["1m"] ?? row.premium_change_pct;
          return <div key={`${row.strike}-${row.option_type}`} className={`grid items-center gap-2 px-4 py-2.5 ${index === 0 ? "bg-cyan-500/[.05]" : ""}`} style={{ gridTemplateColumns: "34px 1fr 54px 64px" }}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-black ${index === 0 ? "bg-cyan-400 text-slate-950" : "bg-slate-800 text-slate-400"}`}>{row.score ?? "—"}</div>
            <div><p className="text-xs font-black text-hi">{fmtPrice(row.strike)} {row.option_type} <span className="ml-1 text-[10px] font-bold text-slate-600">{row.moneyness}</span></p><p className="text-[10px] tabular-nums text-slate-500">₹{fmtPrice(row.last_price, 1)} · V {signed(row.vwap_distance_pct)} · Δ {row.delta == null || Number.isNaN(Number(row.delta)) ? "—" : fmtPrice(Math.abs(Number(row.delta)), 2)} · IV {row.iv == null ? "—" : `${fmtPrice(row.iv, 1)}%`}</p></div>
            <div className="text-right"><p className={`text-[10px] font-black ${Number(velocity || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{signed(velocity)}</p><p className="text-[10px] text-slate-600">1m vel</p></div>
            <div className="text-right"><p className="text-[10px] font-black text-slate-300">{row.spread_pct != null ? `${fmtPrice(row.spread_pct, 1)}%` : "—"}</p><p className="text-[10px] text-slate-600">spread</p></div>
          </div>;
        })}
      </div> : <EmptyData label="Strike rankings appear with the enriched live chain" />}
    </div>
  );
});
